if (!customElements.get('draggable-cards')) {
  class DraggableCards extends HTMLElement {
    constructor() {
      super();
      
      // Physics constants
      this.FRICTION = 0.95;
      this.MIN_VELOCITY = 0.1;
      this.THROW_MULTIPLIER = 0.3;
      this.RESIZE_DEBOUNCE = 100;
      
      // State
      this.cardStates = [];
      this.activeCard = null;
      this.hoveredCardIndex = null; // 跟踪当前 hover 的卡片索引
      this.hasUserDragged = false; // 追踪用户是否进行过拖动操作
      this.startX = 0;
      this.startY = 0;
      this.lastX = 0;
      this.lastY = 0;
      this.lastTime = 0;
      this.velocityX = 0;
      this.velocityY = 0;
      this.resizeRAF = null;
      this.lastResizeTime = 0;
      
      // Bound methods for event listeners
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onCardHover = this.onCardHover.bind(this);
      this.onCardLeave = this.onCardLeave.bind(this);
      this.onArenaLeave = this.onArenaLeave.bind(this);
      this.onResize = this.onResize.bind(this);
      this.animate = this.animate.bind(this);
    }
    
    connectedCallback() {
      this.arena = this.querySelector('[data-cards-arena]');
      this.cards = this.querySelectorAll('.draggable-cards__card');
      
      if (!this.arena || this.cards.length === 0) return;
      if(this.shouldEnableDrag()) {
        this.initCards();
        this.bindEvents();
      }
    }
    
    disconnectedCallback() {
      this.unbindEvents();
    }
    
    isMobile() {
      return window.innerWidth <= 900;
    }
    
    /**
     * 检测是否是平板设备（iPad 或其他 tablet）
     * 平板设备需要支持触摸拖动功能
     */
    isTablet() {
      const isTabletWidth = window.innerWidth >= 901 && window.innerWidth <= 1370;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      // iPad 特殊检测（iOS 13+ 的 iPad 会报告为 MacIntel）
      return isTabletWidth && hasTouchScreen;
    }
    
    /**
     * 检测是否应该启用拖动功能
     * 桌面端和平板端启用，手机端禁用
     */
    shouldEnableDrag() {
      // 平板设备始终启用
      if (this.isTablet()) return true;
      // 非移动端（桌面）启用
      return !this.isMobile();
    }
    
    initCards() {
      const arenaRect = this.arena.getBoundingClientRect();
      const mobile = this.isMobile();
      
      this.cards.forEach((card, index) => {
        const widthDesktop = parseInt(card.clientWidth) || 300;
        const heightDesktop = parseInt(card.clientWidth) || 300;
        const widthMobile = parseInt(card.clientWidth) || 150;
        const heightMobile = parseInt(card.clientWidth) || 150;
        
        const width = mobile ? widthMobile : widthDesktop;
        const height = mobile ? heightMobile : heightDesktop;
        
        const cardRect = card.getBoundingClientRect();
      
        const initialX = cardRect.left - arenaRect.left;
        const initialY = cardRect.top - arenaRect.top;
        
        this.cardStates[index] = {
          x: arenaRect.width / 2 - width / 2,
          y: arenaRect.height / 2 - height / 2,
          vx: 0,
          vy: 0,
          width: width,
          height: height,
          isDragging: false,
          isAnimating: false,
          hasEntryAnimated: false,
          rotateX: 0,
          rotateY: 0,
          initialX,
          initialY
        };
        
        // Set initial size
        card.style.width = width + 'px';
        card.style.height = height + 'px';
        card.style.left = `${initialX}px`;
        card.style.top = `${initialY}px`;
      });
    }
    
    updateCardPosition(card, x, y, initialX, initialY) {
      card.style.transform = `translate(${x - initialX}px, ${y - initialY}px)`;
    }
    
    updateCardInnerTilt(card, rotateX, rotateY) {
      const inner = card.querySelector('.draggable-cards__card-inner');
      if (inner) {
        // 保留 translateZ(0) 确保 Safari 中保持 GPU 合成层，防止闪烁
        inner.style.transform = `translateZ(0) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      }
    }
    
    calculateTilt(mouseX, mouseY, card, state) {
      const cardRect = card.getBoundingClientRect();
      const centerX = cardRect.left + cardRect.width / 2;
      const centerY = cardRect.top + cardRect.height / 2;
      
      const offsetX = mouseX - centerX;
      const offsetY = mouseY - centerY;
      
      const mathX = offsetX;
      const mathY = -offsetY;
      
      const halfWidth = cardRect.width / 2;
      const halfHeight = cardRect.height / 2;
      const maxRotation = 25;
      
      const signRotateX = mathY >= 0 ? 1 : -1;
      const signRotateY = mathX >= 0 ? 1 : -1;
      
      const magRotateX = Math.abs(mathX) / halfWidth * maxRotation;
      const magRotateY = Math.abs(mathY) / halfHeight * maxRotation;
      
      let rotateXValue = signRotateX * magRotateX;
      let rotateYValue = signRotateY * magRotateY;
      
      rotateXValue = Math.max(-maxRotation, Math.min(maxRotation, rotateXValue));
      rotateYValue = Math.max(-maxRotation, Math.min(maxRotation, rotateYValue));
      
      state.rotateX = rotateXValue;
      state.rotateY = rotateYValue;
      
      return { rotateX: rotateXValue, rotateY: rotateYValue };
    }
    
    clampPosition(state, arenaWidth, arenaHeight) {
      const minX = 0;
      const minY = 0;
      const maxX = arenaWidth - state.width;
      const maxY = arenaHeight - state.height;
      
      if (state.x < minX) {
        state.x = minX;
        state.vx *= -0.5;
      }
      if (state.x > maxX) {
        state.x = maxX;
        state.vx *= -0.5;
      }
      if (state.y < minY) {
        state.y = minY;
        state.vy *= -0.5;
      }
      if (state.y > maxY) {
        state.y = maxY;
        state.vy *= -0.5;
      }
    }
    
    animate() {
      const arenaRect = this.arena.getBoundingClientRect();
      let needsAnimation = false;
      
      this.cards.forEach((card, index) => {
        const state = this.cardStates[index];
        
        if (state.isDragging) return;
        
        if (Math.abs(state.vx) > this.MIN_VELOCITY || Math.abs(state.vy) > this.MIN_VELOCITY) {
          state.x += state.vx;
          state.y += state.vy;
          
          state.vx *= this.FRICTION;
          state.vy *= this.FRICTION;
          
          this.clampPosition(state, arenaRect.width, arenaRect.height);
          this.updateCardPosition(card, state.x, state.y, state.initialX, state.initialY);
          needsAnimation = true;
        }
      });
      
      if (needsAnimation) {
        requestAnimationFrame(this.animate);
      }
    }
    
    onPointerDown(e) {
      const card = e.target.closest('.draggable-cards__card');
      if (!card) return; // 没有点击到卡片，允许页面正常滚动
      
      const index = parseInt(card.dataset.cardIndex);
      const state = this.cardStates[index];
      
      this.activeCard = { card, index, state };
      state.isDragging = true;
      this.hasUserDragged = true; // 标记用户已进行拖动操作
      
      // 只改变当前点击的 card 的层级，使用递增的 z-index 确保最后点击的在最上层
      this.currentZIndex = (this.currentZIndex || 100) + 1;
      card.style.zIndex = this.currentZIndex;
      
      const arenaRect = this.arena.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      
      const currentX = cardRect.left - arenaRect.left;
      const currentY = cardRect.top - arenaRect.top;
      
      state.x = currentX;
      state.y = currentY;
      
      const clientX = e.clientX || e.touches[0].clientX;
      const clientY = e.clientY || e.touches[0].clientY;
      
      this.startX = clientX - cardRect.left;
      this.startY = clientY - cardRect.top;
      
      card.style.transition = 'none';
      
      this.updateCardPosition(card, currentX, currentY, state.initialX, state.initialY);
      
      this.lastX = clientX;
      this.lastY = clientY;
      this.lastTime = Date.now();
      this.velocityX = 0;
      this.velocityY = 0;
      
      card.classList.add('dragging');
      
      // 只有当用户触摸到卡片时才阻止默认行为（阻止页面滚动）
      if (e.cancelable) {
        e.preventDefault();
      }
    }
    
    onPointerMove(e) {
      if (!this.activeCard) return; // 没有正在拖动的卡片，允许页面正常滚动
      
      const { card, state } = this.activeCard;
      const arenaRect = this.arena.getBoundingClientRect();
      
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const clientY = e.clientY || (e.touches && e.touches[0].clientY);
      
      if (clientX === undefined || clientY === undefined) return;
      
      const now = Date.now();
      const dt = now - this.lastTime;
      if (dt > 0) {
        this.velocityX = (clientX - this.lastX) / dt * 16;
        this.velocityY = (clientY - this.lastY) / dt * 16;
      }
      
      this.lastX = clientX;
      this.lastY = clientY;
      this.lastTime = now;
      
      state.x = clientX - arenaRect.left - this.startX;
      state.y = clientY - arenaRect.top - this.startY;
      
      this.clampPosition(state, arenaRect.width, arenaRect.height);
      this.updateCardPosition(card, state.x, state.y, state.initialX, state.initialY);
      
      this.calculateTilt(clientX, clientY, card, state);
      this.updateCardInnerTilt(card, state.rotateX, state.rotateY);
      
      // 只在拖动卡片时阻止默认行为
      if (e.cancelable) {
        e.preventDefault();
      }
    }
    
    onPointerUp(e) {
      if (!this.activeCard) return;
      
      const { card, state } = this.activeCard;
      
      state.isDragging = false;
      card.classList.remove('dragging');
      
      // 不重置 z-index，保持卡片在最上层
      // z-index 会在用户滚动离开模块后由 resetAllCards() 统一恢复
      
      state.vx = this.velocityX * this.THROW_MULTIPLIER;
      state.vy = this.velocityY * this.THROW_MULTIPLIER;
      
      state.rotateX = 0;
      state.rotateY = 0;
      this.updateCardInnerTilt(card, 0, 0);
      
      this.activeCard = null;
      
      requestAnimationFrame(this.animate);
    }
    
    onCardHover(e) {
      if (this.activeCard) return;
      
      const card = e.target.closest('.draggable-cards__card');
      const currentIndex = card ? parseInt(card.dataset.cardIndex) : null;
      
      // 如果鼠标移到了不同的卡片或离开了所有卡片，重置前一个卡片的 rotate
      if (this.hoveredCardIndex !== null && this.hoveredCardIndex !== currentIndex) {
        const prevCard = this.cards[this.hoveredCardIndex];
        const prevState = this.cardStates[this.hoveredCardIndex];
        if (prevCard && prevState) {
          prevState.rotateX = 0;
          prevState.rotateY = 0;
          this.updateCardInnerTilt(prevCard, 0, 0);
        }
      }
      
      // 更新当前 hover 的卡片索引
      this.hoveredCardIndex = currentIndex;
      
      // 如果不在任何卡片上，直接返回
      if (!card) return;
      
      const state = this.cardStates[currentIndex];
      if (!state) return;
      
      this.calculateTilt(e.clientX, e.clientY, card, state);
      this.updateCardInnerTilt(card, state.rotateX, state.rotateY);
    }
    
    onCardLeave(e) {
      const card = e.target.closest('.draggable-cards__card');
      if (!card) return;
      
      const index = parseInt(card.dataset.cardIndex);
      const state = this.cardStates[index];
      if (!state) return;
      
      // 清除 hover 状态
      if (this.hoveredCardIndex === index) {
        this.hoveredCardIndex = null;
      }
      
      // 无条件重置 rotate（移除速度检查，确保总是重置）
      state.rotateX = 0;
      state.rotateY = 0;
      this.updateCardInnerTilt(card, 0, 0);
    }
    
    /**
     * 重置所有卡片的 hover 倾斜效果
     * 用于处理鼠标快速离开 arena 等边缘情况
     */
    resetAllHoverTilts() {
      this.hoveredCardIndex = null;
      this.cards.forEach((card, index) => {
        const state = this.cardStates[index];
        if (state && !state.isDragging) {
          state.rotateX = 0;
          state.rotateY = 0;
          this.updateCardInnerTilt(card, 0, 0);
        }
      });
    }
    
    /**
     * 鼠标离开 arena 区域时的处理
     * 确保所有卡片的 rotate 都被重置
     */
    onArenaLeave(e) {
      // 如果正在拖动，不处理
      if (this.activeCard) return;
      
      // 重置所有 hover 倾斜效果
      this.resetAllHoverTilts();
    }
    
    handleResize() {
      const arenaRect = this.arena.getBoundingClientRect();
      const mobile = this.isMobile();
      
      this.cards.forEach((card, index) => {
        const state = this.cardStates[index];
        const widthDesktop = parseInt(card.dataset.widthDesktop) || 300;
        const heightDesktop = parseInt(card.dataset.heightDesktop) || 300;
        const widthMobile = parseInt(card.dataset.widthMobile) || 150;
        const heightMobile = parseInt(card.dataset.heightMobile) || 150;
        
        state.width = mobile ? widthMobile : widthDesktop;
        state.height = mobile ? heightMobile : heightDesktop;
        
        card.style.width = state.width + 'px';
        card.style.height = state.height + 'px';
        
        this.clampPosition(state, arenaRect.width, arenaRect.height);
        this.updateCardPosition(card, state.x, state.y, state.initialX, state.initialY);
        
        state.rotateX = 0;
        state.rotateY = 0;
        this.updateCardInnerTilt(card, 0, 0);
      });
    }
    
    checkResize(currentTime) {
      if (currentTime - this.lastResizeTime >= this.RESIZE_DEBOUNCE) {
        this.handleResize();
        this.resizeRAF = null;
      } else {
        this.resizeRAF = requestAnimationFrame(this.checkResize.bind(this));
      }
    }
    
    onResize() {
      this.lastResizeTime = performance.now();
      if (!this.resizeRAF) {
        this.resizeRAF = requestAnimationFrame(this.checkResize.bind(this));
      }
    }
    
    /**
     * 检测设备是否支持触摸
     */
    hasTouchSupport() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
    
    bindEvents() {
      // 桌面端绑定拖动和 hover 效果
      this.arena.addEventListener('mousedown', this.onPointerDown);
      document.addEventListener('mousemove', this.onPointerMove);
      document.addEventListener('mouseup', this.onPointerUp);
      this.arena.addEventListener('mousemove', this.onCardHover);
      this.arena.addEventListener('mouseleave', this.onArenaLeave);
      
      this.cards.forEach(card => {
        card.addEventListener('mouseleave', this.onCardLeave);
      });
      
      // 支持触摸的设备绑定触摸事件（包括 iPad 横屏/竖屏）
      // 不再依赖 isTablet() 的宽度判断，避免横屏时触摸事件丢失
      if (this.hasTouchSupport()) {
        this.arena.addEventListener('touchstart', this.onPointerDown, { passive: false });
        document.addEventListener('touchmove', this.onPointerMove, { passive: false });
        document.addEventListener('touchend', this.onPointerUp, { passive: true });
      }
      
      window.addEventListener('resize', this.onResize);
    }
    
    unbindEvents() {
      this.arena.removeEventListener('mousedown', this.onPointerDown);
      document.removeEventListener('mousemove', this.onPointerMove);
      document.removeEventListener('mouseup', this.onPointerUp);
      this.arena.removeEventListener('mousemove', this.onCardHover);
      this.arena.removeEventListener('mouseleave', this.onArenaLeave);
      
      this.cards.forEach(card => {
        card.removeEventListener('mouseleave', this.onCardLeave);
      });
      
      // 移除触摸事件
      if (this.hasTouchSupport()) {
        this.arena.removeEventListener('touchstart', this.onPointerDown);
        document.removeEventListener('touchmove', this.onPointerMove);
        document.removeEventListener('touchend', this.onPointerUp);
      }
      
      window.removeEventListener('resize', this.onResize);
      
      if (this.resizeRAF) {
        cancelAnimationFrame(this.resizeRAF);
      }
    }
    
    /**
     * 公开方法：重置所有卡片到初始状态
     * 用于外部调用（如 ScrollMagic leave 事件）
     * 会停止所有正在进行的动画和拖动操作
     */
    resetAllCards() {
      // 停止任何正在进行的拖动
      if (this.activeCard) {
        const { card, state } = this.activeCard;
        state.isDragging = false;
        card.classList.remove('dragging');
        this.activeCard = null;
      }
      
      // 清除 hover 状态
      this.hoveredCardIndex = null;
      
      // 重置 z-index 计数器
      this.currentZIndex = 100;
      
      // 重置所有卡片状态
      this.cards.forEach((card, index) => {
        const state = this.cardStates[index];
        if (!state) return;
        
        // 停止速度动画， 重要: 核心代码，用于停止 animate 动画，如果 animate 动画还在运行，可能会导致卡片无法重置到初始位置
        state.vx = 0;
        state.vy = 0;
        state.isDragging = false;
        state.isAnimating = false;
        
        // 重置倾斜效果
        state.rotateX = 0;
        state.rotateY = 0;
        this.updateCardInnerTilt(card, 0, 0);
        
        // 移除 dragging 类
        card.classList.remove('dragging');
        
        // 重置 z-index
        card.style.removeProperty('z-index');
      });
    }
  }
  
  customElements.define('draggable-cards', DraggableCards);
}

$(window).ready(function() {
  
  var draggableCardsController = null;
  var leaveObserver = null;

  function initScrollMagic() {
    draggableCardsController = new ScrollMagic.Controller();
    
    // 入场动画 - 只触发一次 (reverse: false)
    new ScrollMagic.Scene({
      triggerElement: ".draggable-cards",
      triggerHook: 0.7,
      reverse: false,
      duration: '100%'
    })
    .on('enter', () => {
      document.querySelectorAll('.draggable-cards__arena').forEach(arena => {
        arena.classList.add('active');
      });
      document.querySelectorAll('.draggable-cards__titles').forEach(titles => {
        titles.classList.add('active');
      });
    })
    .addTo(draggableCardsController);
    
    // PC端和平板端：使用 Intersection Observer 监听离开事件（可重复触发）
    const draggableCardsEl = document.querySelector('draggable-cards');
    if (draggableCardsEl && typeof draggableCardsEl.shouldEnableDrag === 'function' && draggableCardsEl.shouldEnableDrag()) {
      initLeaveObserver();
    }
  }
  
  function initLeaveObserver() {
    const draggableCardsSection = document.querySelector('.draggable-cards');
    if (!draggableCardsSection) return;
    
    leaveObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // 当模块离开视口时
        if (!entry.isIntersecting) {
          const draggableCardsEl = document.querySelector('draggable-cards');
          // 只有当用户进行过拖动操作时才重置
          if (draggableCardsEl && draggableCardsEl.hasUserDragged) {
            console.log('leave - user has dragged, resetting cards');
            resetCards();
            // 重置拖动标记，以便下次可以再次检测
            draggableCardsEl.hasUserDragged = false;
          }
        }
      });
    }, {
      threshold: 0, // 当元素完全离开视口时触发
      rootMargin: '0px'
    });
    
    leaveObserver.observe(draggableCardsSection);
  }
  
  function resetCards() {
    const cards = document.querySelectorAll('.draggable-cards__card');
    const draggableCardsEl = document.querySelector('draggable-cards');
    
    if (draggableCardsEl && typeof draggableCardsEl.resetAllCards === 'function') {
      draggableCardsEl.resetAllCards();
    }
    
    cards.forEach(card => {
      card.style.removeProperty('transition');
      card.style.removeProperty('transform');
    });
  }

  initScrollMagic();
})